<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { useContentCollection, useMyCollectionsStore } from '@ilteducation/content-collections';
import type { Member, Identity } from '@ilteducation/content-collections-types';
import { ShareEntity, ShareDialog as MemberSelectionDialog, ShareSettingsItem } from '@ilteducation/components';
import { useLocale } from '@/plugins/translation';
import { useFetchUsers, useFetchGroups } from '@ilteducation/customer-mgmt';
import { useUserContext } from '@ilteducation/ilt-auth';
import useSnackbar from '@/composables/use-snackbar';

type Props = {
  contentCollectionId: string;
};

const props = defineProps<Props>();
const { contentCollectionId } = toRefs(props);

const { t, l } = useLocale();
const { user } = useUserContext();
const myCollectionsStore = useMyCollectionsStore();
const { update } = myCollectionsStore;
const { collection, isOwner } = useContentCollection(contentCollectionId);
const { fetchUsersByIds } = useFetchUsers();
const { fetchGroupsByIds } = useFetchGroups();
const { snackbarSuccess, snackbarError } = useSnackbar();

const collectionUrl = computed(() => window.location.href);
const isAddOwnerDialogActive = ref(false);
const isAddMembersDialogActive = ref(false);
const isSharing = ref(false);
const isLoadingPublicToggle = ref(false);

const checkAccessToAdd = computed(() => isOwner.value);

const memberGroupIds = computed(
  () => collection.value?.members?.filter((member) => member.type === 'group').map((group) => group.id) || [],
);
const memberUserIds = computed(
  () => collection.value?.members?.filter((member) => member.type === 'user').map((u) => u.id) || [],
);
const ownersUserIds = computed(() => collection.value?.owners?.map((o) => o.userId) || []);

const updateCollectionParticipants = async (participants: Record<string, Member[] | Identity[]>) => {
  isSharing.value = true;
  try {
    await update(props.contentCollectionId, participants);
    isAddOwnerDialogActive.value = false;
    isAddMembersDialogActive.value = false;
    snackbarSuccess(t(l.content_collections.share_complete_message));
  } catch (error) {
    snackbarError('Failed to update collection participants');
    console.error('Failed to update collection participants:', error);
  } finally {
    isSharing.value = false;
  }
};

const updateMembers = (selectedUserIds: Member[]) => {
  updateCollectionParticipants({ members: selectedUserIds });
};

const updateOwners = async (owners: Identity[]) => {
  if (owners.length === 0) {
    snackbarError(t(l.content_collections.owners_error_message));
    return;
  }
  await updateCollectionParticipants({ owners });
};

const toggleCollectionPublic = async () => {
  if (!collection.value) return;
  isLoadingPublicToggle.value = true;
  try {
    await update(collection.value.id, { isPublic: !collection.value.isPublic });
  } catch (error) {
    snackbarError('Failed to update collection visibility');
    console.error('Failed to update collection visibility:', error);
  } finally {
    isLoadingPublicToggle.value = false;
  }
};

const prepareAndUpdateOwners = (selectedUsers: Member[]) => {
  const newOwners: Identity[] = selectedUsers.map((u: Member) => ({
    userId: u.id,
  }));
  updateOwners(newOwners);
};

const removeOwner = (id: string) => {
  if (!collection.value?.owners) return;
  const updatedOwners = collection.value.owners.filter((o) => o.userId !== id);
  updateOwners(updatedOwners);
};

const removeMember = (id: string) => {
  if (!collection.value?.members) return;
  updateMembers(collection.value.members.filter((m) => m.id !== id));
};

const removeAllMembers = () => {
  updateMembers([]);
};

const sharingOptions = [
  {
    key: 'components.google_classroom',
    title: 'components.label_google_classroom',
    bgColor: '#558b2f',
    icon: 'mdi-google-classroom',
    link: 'https://classroom.google.com/share?itemtype=material&url=',
  },
  {
    key: 'microsoft_teams',
    title: 'components.label_microsoft_teams',
    bgColor: '#4951B9',
    icon: 'mdi-microsoft-teams',
    link: 'https://teams.microsoft.com/share?href=',
  },
  {
    key: 'email',
    title: 'components.label_email',
    bgColor: '#AEE3DA',
    icon: 'mdi-email-outline',
    callback: () => {
      const subject = encodeURIComponent(collection.value?.name || 'Collection');
      const body = encodeURIComponent(`${collection.value?.name}\n\n${collectionUrl.value}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
  },
];
</script>

<template>
  <div v-if="collection" class="rounded-xl bg-surface-bright px-5 pb-5 pt-0">
    <ShareEntity
      :members="collection.members?.length ? collection.members.map((m) => ({ id: m.id, type: m.type }) as Member) : []"
      :owners="collection.owners ? collection.owners.map((o) => ({ id: o.userId, type: 'user' }) as Member) : []"
      :can-add-members="checkAccessToAdd"
      :entity-url="collectionUrl"
      :entity-share-url="collectionUrl"
      :entity-title="collection.name"
      :can-remove-owners="(collection.owners?.length ?? 0) > 1"
      :hide-sharing-options="false"
      :fetch-users="fetchUsersByIds"
      :fetch-groups="fetchGroupsByIds"
      :is-available-to-share="collection.isPublic ?? false"
      :with-default-sharing-options="false"
      :sharing-options="sharingOptions"
      :is-loading="isSharing"
      rounded="lg"
      density="comfortable"
      @add-members="isAddMembersDialogActive = true"
      @remove-member="removeMember"
      @remove-all-members="removeAllMembers"
      @remove-owner="removeOwner"
      @add-owners="isAddOwnerDialogActive = true"
    >
      <template v-if="!collection.isPublic" #subtitle>
        <p class="mb-2">
          {{ t(l.content_collections.message_make_public_info) }}
        </p>
      </template>
      <template #title-right>
        <ShareSettingsItem
          :id="`public-settings-item`"
          class="pa-0"
          :prepend="true"
          icon-name="mdi-web"
          :label="`${t(l.label_status)}: ${
            collection.isPublic ? t(l.share.label_status_public) : t(l.share.label_status_private)
          }`"
          :loading="isLoadingPublicToggle"
          :value="collection.isPublic ?? false"
          :hidden-label="true"
          @change="toggleCollectionPublic"
        />
      </template>
    </ShareEntity>

    <!-- Owners dialog-->
    <MemberSelectionDialog
      v-if="collection"
      v-model="isAddOwnerDialogActive"
      :user-id="user.uid"
      :user-type="'employee'"
      :share-name="collection.name"
      :can-share-with-group="false"
      :is-sharing="isSharing"
      :member-user-ids="ownersUserIds"
      :member-group-ids="[]"
      dialog-title="components.label_add_owners"
      @close="isAddOwnerDialogActive = false"
      @update-participants="prepareAndUpdateOwners"
    />

    <!-- Members dialog-->
    <MemberSelectionDialog
      v-if="collection"
      v-model="isAddMembersDialogActive"
      :user-id="user.uid"
      :share-name="collection.name"
      :is-sharing="isSharing"
      :member-user-ids="memberUserIds"
      :member-group-ids="memberGroupIds"
      dialog-title="components.label_add_members"
      @close="isAddMembersDialogActive = false"
      @update-participants="updateMembers"
    />
  </div>
</template>

<style scoped lang="scss"></style>
